import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { CITATION_STYLES, getCitationStyle, type CitationStyle } from "@/lib/citation-styles";

export const Route = createFileRoute("/education/writing/$style")({
  loader: ({ params }) => {
    const style = getCitationStyle(params.style);
    if (!style) throw notFound();
    return { style };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.style;
    if (!s) return { meta: [{ title: "Citation style not found" }] };
    return {
      meta: [
        {
          title: `${s.shortName} — Legal Writing Academy | Nkadimeng Danny`,
        },
        {
          name: "description",
          content: `${s.shortName} citation guide for South African legal writing: rules, worked examples and common pitfalls. ${s.summary.slice(0, 110)}`,
        },
        { property: "og:title", content: `${s.shortName} citation guide` },
        { property: "og:description", content: s.summary.slice(0, 160) },
      ],
    };
  },
  notFoundComponent: () => (
    <section className="container-narrow py-16 text-center">
      <h1 className="font-display text-3xl text-navy">Style not found</h1>
      <Link
        to="/education/writing"
        className="mt-4 inline-flex items-center gap-1 text-navy underline"
      >
        ← Back to the Writing Academy
      </Link>
    </section>
  ),
  errorComponent: ({ error }) => (
    <section className="container-narrow py-16 text-center">
      <h1 className="font-display text-3xl text-navy">Something went wrong</h1>
      <p className="mt-2 text-muted-foreground">{error.message}</p>
    </section>
  ),
  component: StylePage,
});

function StylePage() {
  const { style } = Route.useLoaderData() as { style: CitationStyle };
  const others = CITATION_STYLES.filter((s) => s.slug !== style.slug);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-12 md:py-16">
          <Link
            to="/education/writing"
            className="inline-flex items-center gap-1 text-sm text-navy hover:underline"
          >
            <ArrowLeft className="size-4" /> Writing Academy
          </Link>
          <p className="mt-4 text-xs uppercase tracking-[0.25em] text-gold">
            Citation style
          </p>
          <h1 className="mt-1 font-display text-3xl text-navy md:text-4xl">
            {style.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            {style.summary}
          </p>
          <Link
            to="/education/writing/tools"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2.5 text-sm font-medium text-white hover:bg-navy/90"
          >
            <Sparkles className="size-4" /> Generate a citation in {style.shortName}
          </Link>
        </div>
      </section>

      <section className="container-narrow py-10">
        <h2 className="font-display text-2xl text-navy">When to use it</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {style.whenToUse.map((w) => (
            <li key={w} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-narrow py-10">
          <h2 className="font-display text-2xl text-navy">Core rules</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {style.rules.map((r) => (
              <div
                key={r.heading}
                className="rounded border border-border bg-card p-4"
              >
                <p className="font-display text-base text-navy">{r.heading}</p>
                <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-narrow py-10">
        <h2 className="font-display text-2xl text-navy">Worked examples</h2>
        <div className="mt-4 space-y-4">
          {style.examples.map((ex, i) => (
            <div
              key={i}
              className="rounded border border-border bg-card p-5"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-gold">
                {ex.label}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                <span className="font-medium text-navy">Raw source:</span>{" "}
                {ex.source}
              </p>
              <p className="mt-3 rounded bg-secondary/60 p-3 font-mono text-sm text-navy">
                {ex.citation}
              </p>
              {ex.notes ? (
                <p className="mt-2 text-xs text-muted-foreground">{ex.notes}</p>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-narrow py-10">
          <h2 className="font-display text-2xl text-navy">Common pitfalls</h2>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {style.pitfalls.map((p) => (
              <li key={p} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-gold" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="container-narrow py-10">
        <h2 className="font-display text-2xl text-navy">Other styles</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {others.map((o) => (
            <Link
              key={o.slug}
              to="/education/writing/$style"
              params={{ style: o.slug }}
              className="rounded-full border border-input bg-card px-4 py-2 text-sm text-navy hover:border-gold"
            >
              {o.shortName} →
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
